import React, {useState} from "react";
import {useSelector} from "react-redux";

import {GENDER_CHOICES, GENDER_ICONS, GENDER_VALUES} from "shared/constants/gender";
import {SMOKER, SMOKING_CHOICES, SMOKING_ICONS, SMOKING_ICONS_SELECTED, SMOKING_VALUES} from "shared/constants/smoking";
import {RATE_CLASSES_BY_HEALTH_VALUE} from "shared/constants/rateClasses";
import {US_STATE_VALUES} from "shared/constants/usState";
import {TABLE_RATING_BY_HEALTH_OPTION} from "shared/constants/tableRating";
import {HEALTH_VALUES_FOR_SMOKERS} from "shared/constants/health";

import ToggleGroup from "shared/components/ToggleGroup";
import DatePicker from "shared/components/DatePicker";
import SelectInput from "shared/components/SelectInput";
import ButtonInput from "shared/components/ButtonInput";
import SideBarIconSection from "shared/components/SideBarIconSection";

import useSetQuoteInput from "apps/QuoterApp/hooks/useSetQuoteInput";
import useNavigate from "apps/QuoterApp/hooks/useNavigate";

import QuoterLayout from "apps/QuoterApp/components/QuoterLayout";
import ContactInfo from "apps/QuoterApp/components/ContactInfo";

import HealthPicker from "./HealthPicker";

import './styles.scss';
import TextInput from "shared/components/TextInput";
import {requestAssistance} from "shared/networking/assistance";
import {createTicket} from "shared/networking/tickets";
import {applyProduct} from "shared/networking/quotes";


function BasicInformationPage() {
    const {goToQuoterResults} = useNavigate();

    const setQuoteInput = useSetQuoteInput();

    const setHealthValue = value => {
        setQuoteInput({
            healthValue: value,
            rateClasses: RATE_CLASSES_BY_HEALTH_VALUE[value],
            tableRating: TABLE_RATING_BY_HEALTH_OPTION[value],
        });
    };

    const validInput = useSelector(state => state.quoteInput.valid);
    const {gender, dob, state, smoking, healthValue} = useSelector(state => state.quoteInput.data);

    // Smokers have different values
    const setSmokingValue = smoking => {
        setQuoteInput({smoking});
        if (smoking === SMOKER && healthValue && !HEALTH_VALUES_FOR_SMOKERS.find(i => i === healthValue)) {
            setHealthValue(healthValue - 1);
        }
    };

    const genderOptions = GENDER_VALUES.map(value => ({
        value,
        text: GENDER_CHOICES[value],
        icon: GENDER_ICONS[value],
    }));
    const smokingOptions = SMOKING_VALUES.map(value => ({
        value,
        text: SMOKING_CHOICES[value],
        icon: SMOKING_ICONS[value],
        iconSelected: SMOKING_ICONS_SELECTED[value],
    }));

    const stateChoices = [
        {value: '', label: 'Choose...'},
        ...US_STATE_VALUES.map(value => ({value, label: value})),
    ];

    const [name, setName] = useState();
    const [assistanceState, setAssistanceState] = useState();
    const [phone, setPhone] = useState();
    const [email, setEmail] = useState();
    const [questions, setQuestions] = useState();
    const [age, setAge] = useState();
    const [loanAmount, setLoanAmount] = useState();
    const [assistanceSubmitted, setAssistanceSubmitted] = useState();

    function renderQuoterContent() {
        return <QuoterLayout.Content className={"BasicInformationPage__Content"}>
            <div className={"Title"}>Let's Get Started</div>

            <div className={"Label"}>What is your gender?</div>
            <ToggleGroup value={gender} items={genderOptions} onChange={gender => setQuoteInput({gender})}/>

            <div className={"Label"}>When is your birthday?</div>
            <DatePicker value={dob} required={true} onChange={dob => setQuoteInput({dob})}/>

            <div className={"Label"}>What state do you live in?</div>
            <SelectInput
              value={state}
              choices={stateChoices}
              required={true}
              onChange={event => setQuoteInput({state: event.target.value})}
            />

            <div className={"Label"}>Do you smoke?</div>
            <ToggleGroup value={smoking} items={smokingOptions} onChange={setSmokingValue}/>

            <div className={"Label"}>Which of these describes best your health?</div>
            <HealthPicker smoking={smoking} onChange={setHealthValue} value={healthValue}/>

            <div className={"BasicInformationPage__Content__SubmitContainer"}>
                <ButtonInput disabled={!validInput} onClick={goToQuoterResults}>
                    View your rates
                </ButtonInput>
            </div>

            <ContactInfo className={"BasicInformationPage__ContactInfo__Content"}/>
        </QuoterLayout.Content>;
    }

    function renderAssistanceSubmitted() {
        return <QuoterLayout.Content className={"BasicInformationPage__Content"}>
            <div className="Container">
                <h2 className="Title">
                    Your information has been submitted!
                </h2>
                <div className="Warning">
                    A Financial Professional will be in touch within 1 business day to review and discuss your options.
                </div>
            </div>
        </QuoterLayout.Content>
    }

    function renderQuoterAssistanceForm() {
        return <QuoterLayout.Content className={"BasicInformationPage__Content"}>
            <h1 className={"RequestAssistanceModal__Title"}>Request Professional Assistance</h1>

            <div className={"RequestAssistanceModal__Label"}>Name</div>
            <TextInput placeholder={"Name"} required={true} value={name} onChange={setName}/>

            <div className={"RequestAssistanceModal__Label"}>State</div>
            <SelectInput
              value={assistanceState}
              choices={stateChoices}
              required={true}
              onChange={event => setAssistanceState(event.target.value)}
            />

            <div className={"RequestAssistanceModal__Label"}>Phone Number</div>
            <TextInput type={'tel'} placeholder={"Phone Number"} required={true} value={phone} onChange={setPhone}/>

            <div className={"RequestAssistanceModal__Label"}>Email</div>
            <TextInput type={'email'} placeholder={"Email"} required={true} value={email} onChange={setEmail}/>

            <div className={"RequestAssistanceModal__Label"}>Age</div>
            <TextInput type={'number'} placeholder={"Age"} required={false} value={age} onChange={setAge}/>

            <div className={"RequestAssistanceModal__Label"}>Loan Amount</div>
            <TextInput type={'number'} placeholder={"Loan Amount"} required={false} value={loanAmount} onChange={setLoanAmount}/>

            <div className={"RequestAssistanceModal__Label"}>Questions/Comments </div>
            <TextInput rows={3} placeholder={"Questions/Comments "} required={false} value={questions} onChange={setQuestions}/>

            <div className={"RequestAssistanceModal__ButtonSection"}>
                <ButtonInput
                  disabled={!name || !assistanceState || !phone || !email}
                  onClick={async () => {
                      await requestAssistance(name, assistanceState, phone, email, questions, age, loanAmount);
                      setAssistanceSubmitted(true);
                  }}
                >
                    Request Assistance
                </ButtonInput>
            </div>
        </QuoterLayout.Content>
    }

    function renderQuoterAssistance() {
        if (assistanceSubmitted) {
            return renderAssistanceSubmitted()
        } else {
            return renderQuoterAssistanceForm()
        }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const allowTickets = urlParams.get('allowTickets') !== '0';
    async function onApplyQuick() {
        try {
            const {ticketUrl} = await createTicket(
              null,
              null,
              null,
              null,
              false,
            );
            window.location.href = ticketUrl;
        } catch (e) {
            console.error(e)
        }
    }

    async function onApplyFull() {
        try {
            const {applicationUrl} = await applyProduct(
              null,
              null,
              null,
              null,
              false,
            );
            window.location.href = applicationUrl;
        } catch (e) {
            console.error(e)
        }
    }

    function renderFormOnly() {
        return <QuoterLayout.Content className={"BasicInformationPage__Content"}>
            <h1 className={"ApplyModal__Title"}>How would you like to proceed?</h1>
            <p>Please select which Secure App Link you'd like to send to your client for completion.</p>
            <div className={"ApplyModal__Options"}>
                <div className={"ApplyModal__Options__Option"}>
                    <ButtonInput className={"ApplyModal__Options__Option__Button"} onClick={onApplyQuick}>
                        Client Pre-Screen Form<br/><i>(2-3 mins)</i>
                    </ButtonInput>
                    <p>The Pre-Screen form will only collect the info necessary for you to shop your case.</p>
                    <br/>
                </div>
                <div className="ApplyModal__Options__Separator"/>
                <div className={"ApplyModal__Options__Option"}>
                    <ButtonInput className={"ApplyModal__Options__Option__Button"} onClick={onApplyFull}>
                        Client Universal Drop-Ticket<br/>
                        <i>(5-7 mins)</i>
                    </ButtonInput>
                    <p>The Pre-App form will collect all the info required to prescreen your case and submit a Drop-Ticket directly to the carrier of your choice.</p>
                </div>
          </div>
        </QuoterLayout.Content>
    }

    /*
    3 different views:
        "assistance" shows a form to request assistance
        "form" skips the quoter and shows two buttons for PreScreen/PreApp flows
        Otherwise shows the Quoter
     */
    const isAssistancePage = window.location.href.indexOf("/assistance/") !== -1;
    const isFormOnlyPage = window.location.href.indexOf("/form/") !== -1;
    let renderMethod = renderQuoterContent;
    if (isAssistancePage) {
        renderMethod = renderQuoterAssistance
    } else if (isFormOnlyPage) {
        renderMethod = renderFormOnly
    }

    return <QuoterLayout className={"BasicInformationPage"}>
        <QuoterLayout.LeftBar>
            <ContactInfo className={"BasicInformationPage__ContactInfo__LeftBar"}/>
        </QuoterLayout.LeftBar>
        {renderMethod()}
        <QuoterLayout.RightBar>
            <SideBarIconSection className={"BasicInformationPage__IconSection"}/>
            <ContactInfo className={"BasicInformationPage__ContactInfo__RightBar"}/>
        </QuoterLayout.RightBar>
    </QuoterLayout>;
}

export default BasicInformationPage;
